/* © 2014 - Copyright of Aetonix Systems Inc - All Rights Reserved. Patent pending.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Aetonix, June 19, 2014
 * For information or permission request, email info@aetonixsystems.com
 */

var Model = require("../types/Model.js");
var xtend = require("xtend");
var Promise = require("promise");

/**
 * This model is used for tracking a person's read notifications(call, conference, text, banner).
 * A notification contains all of the messages, calls and banners that a person has received
 * @type {Model}
 */

module.exports = Model({
	name: "readnotifications",
	init: init,
	uninit: uninit,

	listeners: listeners,
	triggers: triggers,

	_page: null,
	_history: null,
	_blocking: null,
	_people: null,
	_ready: false,
	_groups: null,

	refresh: refresh,
	add_ids: add_ids,
	recalculate: recalculate,
});

function listeners() {
	return {
		"livechatgroups:updated": handle_livechatgroups_updated,
		"groups:updated": handle_groups_updated,
		"blocking:updated": handle_blocking_updated,
		"people:updated": handle_people_updated,
		"connectivity:online": refresh,
		"calling:updated": refresh,
		"mqtt:$/notifications/cleared/all": recalculate,
		"loadchecker:ready": initial_load,
		"localization:updated": handle_localization_updated
	};
}

function initial_load() {
	this._ready = true;
	this.refresh();
}

function triggers() {
	return {
		more: more,
		add: add_unread_notification,
		refresh: refresh,
	};
}

function init() {
	this.push([]);
	this._page = 0;
	this._history = [];
	this._blocking = [];
	this._people = {};
	this._groups = {};
	this._language = "";
}

function uninit() {
	this._history = null;
	this._blocking = null;
	this._people = null;
	this._page = null;
}


function refresh() {
	if(!this._ready) return Promise.resolve([]);
	var _page = 0;
	this._page = _page;
	this.emit("save:trigger:start");
	return this.api.notifications.list.read(this._page)
		.then(handle_load.bind(this))
		.catch(console.error.bind(console))
		.then(finishLoad.bind(this));
}

function handle_load(list) {
	this._history = list;
	this.add_ids(this._history);
	this.recalculate();
}

function finishLoad() {
	this.emit("save:trigger:finish");
}

function more() {
	this.emit("save:trigger:start");
	this._page++;
	this.api.notifications.list.read(this._page)
		.then(handle_more.bind(this))
		.catch(console.error.bind(console))
		.then(finishLoad.bind(this));
	this.emit("usermetrics:trigger:track", "load more notification history");

}

function handle_localization_updated(localization) {
	this._language = localization.language;
}

function handle_more(list) {
	this.add_ids(list);
	this._history = this._history.concat(list);
	this.recalculate();
}

function recalculate() {
	var people = this._people;
	var history = this._history;
	var blocking = this._blocking;
	var groups = this._groups;
	var language = this._language;
	var livechats = this._livechats;

	var merged = history.reduce(function (list, item) {
		var update = {
			content: process_content(item.content),
			updated_at: timestamp(item.updated_at),
			formatted_date: formatDate(item.updated_at, checkLanguage(language)),
		};
		var from = item.from;
		var to = item.content.to;
		var acknowledge = item.content.acknowledge;

		if (is_conference(item)) {
			var from0 = from[0];
			var from1 = from[1];
			if (blocking.indexOf(from0) !== -1) return list;
			if (blocking.indexOf(from1) !== -1) return list;

			var person0 = people[from0];
			var person1 = people[from1];

			if (!(person0 && person1)) return list;

			update.from = [person0, person1];
		} else if (is_livechat(item)) {
			if (item.type.indexOf("systemmessage") !== -1) {
				const systemPerson = item.metadata.data;
				update.from = "system";
				const personObj = people[systemPerson];
				if (!personObj) return list;
				update.metadata = {
					type: item.metadata.type,
					data: personObj
				};
			} else {
				const person = people[from];
				if(!person) return list;
				update.from = person;
			}
			const group = livechats[item.group];
			if(!group) return list;
			update.group = group;
		}	else if (is_group(item)){
			if(item.type.indexOf("systemmessage") !== -1){
				const systemPerson =  item.metadata.data;
				update.from = "system";
				const personObj = people[systemPerson];
				if(!personObj) return list;
				update.metadata = {
					type: item.metadata.type,
					data: personObj
				};
			} else {
				var member = people[from];
				if(!member) return list;
				update.from = member;
			}
			const group = groups[item.group];
			if(!group) return list;
			update.group = group;
		} else {
			if (blocking.indexOf(from) !== -1) return list;
			const person = people[from];
			if (!person) return list;
			update.from = person;
			if (blocking.indexOf(to) !== -1) return list;
			if(to){
				var personTo = people[to];
				update.content = xtend(item.content, {
					to: personTo
				});
			}
			if (blocking.indexOf(acknowledge) !== -1) return list;
			if(acknowledge){
				var personAcknowledge = people[acknowledge];
				update.content = xtend(item.content, {
					acknowledge: personAcknowledge
				});
			}
		}

		list.push(xtend(item, update));

		return list;
	}, []);
	this.push(merged);
}


function add_ids(list) {
	var ids = get_ids(list);
	this.emit("people:trigger:add", {
		who: ids,
		lean: true
	});
}

/**
 * Fetches out all the unique ids in a list of timline items
 * @param  {Array} list The raw page items, each having a `from` property
 * @return {Array}      List of unique `from` ids in the items
 */
function get_ids(list) {
	return Object.keys(list.reduce(function(result, item) {
		var from = item.from;
		var to = item.content.to;
		var acknowledge = item.content.acknowledge;
		var metadata = item.metadata;
		if (is_conference(item)) {
			result[from[0]] = true;
			result[from[1]] = true;
		} else result[from] = true;

		if(to && Array.isArray(to)){
			to.forEach(function(id){
				result[id] = true;
			});
		} else if (to) result[to] = true;
		if (acknowledge) result[acknowledge] = true;

		if(item.type.indexOf("systemmessage") && metadata){
			var data = item.metadata.data;
			result[data] = true;
		}

		return result;
	}, {}));}


// Converts a date timestamp into a human readable timestamp
function timestamp(value) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	var res = "";
	res += padNumber(d.getHours());
	res += ":" + padNumber(d.getMinutes());
	return res;
}

function checkLanguage(lang){
	if(lang === "cn_s")
		return "zh-CN";
	else if(lang === "cn_t")
		return "zh-HK";
	else
		return lang;
}
// Converts a date timestamp into a human readable date
function formatDate(value, language) {
	if (!value) return ""; // Ignore empty values
	var d = new Date(value);
	return d.toLocaleDateString(language, getDateLocaleOptions());
}

function getDateLocaleOptions(){
	return {
		day: "numeric",
		month: "short"
	};
}

// Pads numbers less than 10 with a 0
function padNumber(number) {
	if (number < 10) return "0" + number;
	return number;
}

function is_conference(item) {
	return item.type.indexOf("conference") > -1;
}

function is_group(item){
	return item.type.indexOf("groupmessage") !== -1;
}

function is_livechat(item){
	return item.type.indexOf("livechat:message") !== -1;
}

function handle_people_updated(people) {
	this._people = people;
	this.recalculate();
}

function handle_groups_updated(groups){
	this._groups = process_groups(groups);
	this.recalculate();
}

function handle_livechatgroups_updated(groups){
	this._livechats = process_groups(groups);
	this.recalculate();
}

function process_groups(groups){
	return groups.reduce(function(newGroups, group){
		var id = group._id;
		newGroups[id] = group;
		return newGroups;
	}, {});
}

function handle_blocking_updated(blocking) {
	this._blocking = blocking;
	this.recalculate();
}

function process_content(content) {
	if (!content) return content;
	if (typeof content === "string") return content;
	if (content.hangup) return xtend(content, {
		hangup: timestamp(content.hangup)
	});
	return content;
}

function add_unread_notification(notification)	{
	var updated = xtend(notification, {
		read: true
	});
	var existing = this._history;
	this._history = [updated].concat(existing);
	this.add_ids(this._history);
	this.recalculate();
}
